import { createAction, handleActions } from 'redux-actions';
import { _getPetTypesList, _addNewPetType, _deletePetType, _getPetTypInfoByID, _editPetType, _getPetTypeActiveInfoByID, _editPetTypeConfigSection } from '~shared/httpService/apis';
import ActionTypes from '~shared/constants/actionTypes';

const initialState = {
  pet_types: { loading: false, response: [], hasError: false, error: {} },
  add_new_pet_type: { loading: false, response: {}, hasError: false, error: {} },
  delete_pet_type: { loading: false, response: {}, hasError: false, error: {} },
  pet_type_info: { loading: false, response: {}, hasError: false, error: {} },
  active_pet_type_info: { loading: false, response: {}, hasError: false, error: {} },
  edit_pet_type: { loading: false, response: {}, hasError: false, error: {} },
  active_pet: null,
  current_pet_details: null
};

export const setActivePet = createAction(ActionTypes.SET_ACTIVE_PET, (pet) => pet);

export const getPetTypesListStart = createAction(ActionTypes.GET_PET_TYPES_LIST);
export const getPetTypesListSuccess = createAction(ActionTypes.GET_PET_TYPES_LIST_SUCCESS, response => response);
export const getPetTypesListError = createAction(ActionTypes.GET_PET_TYPES_LIST_ERROR, error => error);

export const getPetTypesList = (page = '', pageSize, searchQuery) => (dispatch) => {
  dispatch(getPetTypesListStart());
  return _getPetTypesList(page, pageSize, searchQuery).then((response) => {
    dispatch(getPetTypesListSuccess(response));
  }).catch((error) => {
    dispatch(getPetTypesListError(error));
  });
};

export const addNewPetTypeStart = createAction(ActionTypes.ADD_NEW_PET_TYPE);
export const addNewPetTypeSuccess = createAction(ActionTypes.ADD_NEW_PET_TYPE_SUCCESS, response => response);
export const addNewPetTypeError = createAction(ActionTypes.ADD_NEW_PET_TYPE_ERROR, error => error);

export const addNewPetType = (data) => (dispatch) => {
  let formdata = new FormData();
  // Only append a real File/Blob — FormData.append converts undefined to the string "undefined"
  if (data?.file instanceof Blob) {
    formdata.append('file', data.file);
  }
  formdata.append('petType', JSON.stringify(data?.petType));
  dispatch(addNewPetTypeStart());
  return _addNewPetType(formdata).then((response) => {
    dispatch(addNewPetTypeSuccess(response));
  }).catch((error) => {
    dispatch(addNewPetTypeError(error));
  });
};

export const deletePetTypeStart = createAction(ActionTypes.DELETE_PET_TYPE);
export const deletePetTypeSuccess = createAction(ActionTypes.DELETE_PET_TYPE_SUCCESS, response => response);
export const deletePetTypeError = createAction(ActionTypes.DELETE_PET_TYPE_ERROR, error => error);

export const deletePetType = (id) => (dispatch) => {
  dispatch(deletePetTypeStart());
  return _deletePetType(id).then((response) => {
    dispatch(deletePetTypeSuccess(response));
  }).catch((error) => {
    dispatch(deletePetTypeError(error));
  });
};

export const getPetTypInfoByIDStart = createAction(ActionTypes.GET_PET_TYPES_INFO_BY_ID);
export const getPetTypInfoByIDSuccess = createAction(ActionTypes.GET_PET_TYPES_INFO_BY_ID_SUCCESS, response => response);
export const getPetTypInfoByIDError = createAction(ActionTypes.GET_PET_TYPES_INFO_BY_ID_ERROR, error => error);

export const getPetTypInfoByID = (id) => (dispatch) => {
  dispatch(getPetTypInfoByIDStart());
  return _getPetTypInfoByID(id).then((response) => {
    dispatch(getPetTypInfoByIDSuccess(response));
  }).catch((error) => {
    dispatch(getPetTypInfoByIDError(error));
  });
};

export const getPetTypeActiveInfoByIDStart = createAction(ActionTypes.GET_ACTIVE_PET_TYPES_INFO_BY_ID);
export const getPetTypeActiveInfoByIDSuccess = createAction(ActionTypes.GET_ACTIVE_PET_TYPES_INFO_BY_ID_SUCCESS, response => response);
export const getPetTypeActiveInfoByIDError = createAction(ActionTypes.GET_ACTIVE_PET_TYPES_INFO_BY_ID_ERROR, error => error);

export const getPetTypeActiveInfoByID = (id) => (dispatch) => {
  dispatch(getPetTypeActiveInfoByIDStart());
  return _getPetTypeActiveInfoByID(id).then((response) => {
    dispatch(getPetTypeActiveInfoByIDSuccess(response));
  }).catch((error) => {
    dispatch(getPetTypeActiveInfoByIDError(error));
  });
};

export const editPetTypeStart = createAction(ActionTypes.EDIT_PET_TYPE);
export const editPetTypeSuccess = createAction(ActionTypes.EDIT_PET_TYPE_SUCCESS, response => response);
export const editPetTypeError = createAction(ActionTypes.EDIT_PET_TYPE_ERROR, error => error);

export const editPetType = (id, data) => (dispatch) => {
  dispatch(editPetTypeStart());

  const sectionEndpoints = {
    breeds: 'breeds',
    hairLengths: 'hair-lengths',
    hairTextures: 'hair-textures',
    combsBlades: 'combs-blades',
    vetInformation: 'vet-information',
    allergies: 'allergies',
    temperaments: 'temperaments',
    desiredHairLengths: 'desired-hair-lengths',
    vaccinationRecords: 'vaccination-records',
    emergencyContactInfo: 'emergency-contact-info',
    documentOptions: 'document-options',
    weightRanges: 'weight-ranges',
    generalPetSizes: 'general-pet-sizes',
    deceaseDate: 'decease-date',
  };

  const payloadKeys = Object.keys(data || {});
  const sectionKeys = payloadKeys.filter((key) =>
    Object.prototype.hasOwnProperty.call(sectionEndpoints, key)
  );
  const hasOnlySectionPayload = payloadKeys.length > 0 && sectionKeys.length === payloadKeys.length;
  const hasBasicDetailsUpdate =
    Object.prototype.hasOwnProperty.call(data || {}, "name") ||
    Object.prototype.hasOwnProperty.call(data || {}, "active") ||
    Object.prototype.hasOwnProperty.call(data || {}, "file");

  const requestPromise = hasOnlySectionPayload
    ? Promise.all(
      sectionKeys.map((sectionKey) =>
        _editPetTypeConfigSection(id, sectionEndpoints[sectionKey], data[sectionKey])
      )
    ).then((responses) => responses[responses.length - 1])
    : hasBasicDetailsUpdate
      ? _editPetType(id, {
        ...(Object.prototype.hasOwnProperty.call(data || {}, "name") ? { name: data?.name } : {}),
        ...(Object.prototype.hasOwnProperty.call(data || {}, "active") ? { active: data?.active } : {}),
        ...(Object.prototype.hasOwnProperty.call(data || {}, "file") ? { file: data?.file } : {}),
      })
    : (() => {
      let formdata = new FormData();
      const payload = { ...(data || {}) };
      if (payload?.file) {
        formdata.append('file', payload.file);
        delete payload.file;
      }
      formdata.append('petType', JSON.stringify(payload));
      return _editPetType(id, formdata);
    })();

  return requestPromise.then((response) => {
    dispatch(editPetTypeSuccess(response));
  }).catch((error) => {
    dispatch(editPetTypeError(error));
  });
};

export const setCurrentPetTypeDetailsSuccess = createAction(ActionTypes.SET_CURRENT_PET_DETAILS, data => data);

export const setCurrentPetTypeDetails = (data) => (dispatch) => {
  dispatch(setCurrentPetTypeDetailsSuccess(data));
};

const reducer = handleActions({
  //SET_ACTIVE_PET
  [ActionTypes.SET_ACTIVE_PET]: (state, action) => ({ ...state, active_pet: action.payload }),
  //PET_TYPES_LIST
  [ActionTypes.GET_PET_TYPES_LIST]: (state) => ({
    ...state, pet_types: {
      ...state.pet_types,
      loading: true, hasError: false, error: {}
    }
  }),
  [ActionTypes.GET_PET_TYPES_LIST_SUCCESS]: (state, action) => ({
    ...state,
    pet_types: {
      ...state.pet_types,
      response: action.payload, loading: false, hasError: false, error: {}
    }
  }),
  [ActionTypes.GET_PET_TYPES_LIST_ERROR]: (state, action) => ({
    ...state,
    pet_types: {
      ...state.pet_types,
      error: action.payload, loading: false, hasError: true, response: {}
    }
  }),
  //ADD_NEW_PET_TYPE
  [ActionTypes.ADD_NEW_PET_TYPE]: (state) => ({
    ...state,
    add_new_pet_type: {
      ...state.add_new_pet_type,
      loading: true, hasError: false, error: {}, response: {}
    }
  }),
  [ActionTypes.ADD_NEW_PET_TYPE_SUCCESS]: (state, action) => ({
    ...state,
    add_new_pet_type: {
      ...state.add_new_pet_type,
      response: action.payload, loading: false, hasError: false, error: {}
    }
  }),
  [ActionTypes.ADD_NEW_PET_TYPE_ERROR]: (state, action) => ({
    ...state,
    add_new_pet_type: {
      ...state.add_new_pet_type,
      error: action.payload, loading: false, hasError: true, response: {}
    }
  }),
  //DELETE_PET_TYPE
  [ActionTypes.DELETE_PET_TYPE]: (state) => ({
    ...state,
    delete_pet_type: {
      ...state.delete_pet_type,
      loading: true, hasError: false, error: {}, response: {}
    }
  }),
  [ActionTypes.DELETE_PET_TYPE_SUCCESS]: (state, action) => ({
    ...state,
    delete_pet_type: {
      ...state.delete_pet_type,
      response: action.payload, loading: false, hasError: false, error: {}
    }
  }),
  [ActionTypes.DELETE_PET_TYPE_ERROR]: (state, action) => ({
    ...state,
    delete_pet_type: {
      ...state.delete_pet_type,
      error: action.payload, loading: false, hasError: true, response: {}
    }
  }),
  //GET_PET_TYPE_INFO_BY_ID
  [ActionTypes.GET_PET_TYPES_INFO_BY_ID]: (state) => ({
    ...state,
    pet_type_info: {
      ...state.pet_type_info,
      loading: true, hasError: false, error: {}, response: {}
    }
  }),
  [ActionTypes.GET_PET_TYPES_INFO_BY_ID_SUCCESS]: (state, action) => ({
    ...state,
    pet_type_info: {
      ...state.pet_type_info,
      response: action.payload, loading: false, hasError: false, error: {}
    }
  }),
  [ActionTypes.GET_PET_TYPES_INFO_BY_ID_ERROR]: (state, action) => ({
    ...state,
    pet_type_info: {
      ...state.pet_type_info,
      error: action.payload, loading: false, hasError: true, response: {}
    }
  }),
  //GET_ACTIVE_PET_TYPES_INFO_BY_ID
  [ActionTypes.GET_ACTIVE_PET_TYPES_INFO_BY_ID]: (state) => ({
    ...state,
    active_pet_type_info: {
      ...state.active_pet_type_info,
      loading: true, hasError: false, error: {}, response: {}
    }
  }),
  [ActionTypes.GET_ACTIVE_PET_TYPES_INFO_BY_ID_SUCCESS]: (state, action) => ({
    ...state,
    active_pet_type_info: {
      ...state.active_pet_type_info,
      response: action.payload, loading: false, hasError: false, error: {}
    }
  }),
  [ActionTypes.GET_ACTIVE_PET_TYPES_INFO_BY_ID_ERROR]: (state, action) => ({
    ...state,
    active_pet_type_info: {
      ...state.active_pet_type_info,
      error: action.payload, loading: false, hasError: true, response: {}
    }
  }),
  //EDIT_PET_TYPE
  [ActionTypes.EDIT_PET_TYPE]: (state) => ({
    ...state,
    edit_pet_type: {
      ...state.edit_pet_type,
      loading: true, hasError: false, error: {}, response: {}
    }
  }),
  [ActionTypes.EDIT_PET_TYPE_SUCCESS]: (state, action) => ({
    ...state,
    edit_pet_type: {
      ...state.edit_pet_type,
      response: action.payload, loading: false, hasError: false, error: {}
    }
  }),
  [ActionTypes.EDIT_PET_TYPE_ERROR]: (state, action) => ({
    ...state,
    edit_pet_type: {
      ...state.edit_pet_type,
      error: action.payload, loading: false, hasError: true, response: {}
    }
  }),
  //EDIT_PET_TYPE
  // Merge with the details collected so far instead of replacing them. Every wizard step
  // saves only the section it owns (plus whatever it could read back from history.state),
  // so a plain replace silently drops sections captured earlier — e.g. the pet type name
  // disappears when the user goes back a step and moves forward again.
  // Preserve petImage File when an update omits it (File/Blob does not always survive
  // history.state after back-navigation).
  [ActionTypes.SET_CURRENT_PET_DETAILS]: (state, action) => {
    if (action.payload == null) {
      return { ...state, current_pet_details: null };
    }
    const prevDetails = state.current_pet_details || {};
    const prevImage = prevDetails?.petImage;
    const nextImage = action.payload?.petImage;
    const petImage =
      nextImage instanceof Blob
        ? nextImage
        : prevImage instanceof Blob
          ? prevImage
          : nextImage ?? prevImage ?? null;

    return {
      ...state,
      current_pet_details: {
        ...prevDetails,
        ...action.payload,
        // never let an absent/undefined value blank out an already collected name
        name: action.payload?.name ?? prevDetails?.name ?? null,
        petImage,
      },
    };
  },
}, initialState);

export default reducer;